import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from "react";
import { ColumnDef, Table as TanTable } from "@tanstack/react-table";
import { AddIcon } from "@chakra-ui/icons";
import { batchCertificate, deleteCertificate, generateCertificate, getCertificates, getCertificateUrl, batchDownload } from "../../services/certificates-service";
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Center,
  Spinner,
  Tag,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";

import { CertificateActions, PageHeading, Panel, Table } from "@bemap/ui";
import { CertificateStatus, formatDate, ICertificate, openUrl, useAuth } from "@bemap/utils";
import { useTranslation } from "react-i18next";
import { MdDownload } from "react-icons/md";

const UploadFileInput: FC<{
  type: string;
  accept: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  id: string;
}> = ({ type, accept, onChange, id }) => {
  return (
    <input
      type={type}
      accept={accept}
      onChange={onChange}
      id={id}
      style={{
        width: '100%',
        padding: '12px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '14px',
      }}
    />
  );
};

const CertificatesList = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { user } = useAuth();

  const initialCertificate: ICertificate = {
    trainerId: user?.id,
    teamId: user?.currentTeamId || 0,
    issueDate: new Date()
  };

  // STATES
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [massDownloading, setMassDownloading] = useState(false);
  const [modalType, setModalType] = useState<"edit" | "new" | "delete">();
  const [certificate, setCertificate] = useState<ICertificate>(initialCertificate);
  const [certificates, setCertificates] = useState<ICertificate[]>([]);
  const [updateData, setUpdateData] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [selectedRows, setSelectedRows] = useState<ICertificate[]>([]);
  const [tableInstance, setTableInstance] = useState<TanTable<ICertificate> | null>(null);

  function handleStatus(status: CertificateStatus) {
    switch (status) {
      case CertificateStatus.WAITING:
        return t('partner.certificates_status_created')
      case CertificateStatus.GENERATED:
        return t('partner.certificates_status_generated')
      default:
        return null
    }
  }

  function handleColor(status: string) {
    switch (status) {
      case t('partner.certificates_status_created'):
        return 'yellow'
      case t('partner.certificates_status_generated'):
        return 'green'
      default:
        return null
    }
  }

  const handleRowSelect = (cert: ICertificate) => {
    setSelectedRows((prevSelected) => {
      if (prevSelected.includes(cert)) {
        return prevSelected.filter((row) => row !== cert);
      } else {
        return [...prevSelected, cert];
      }
    });
  };

  const columns = useMemo<ColumnDef<ICertificate, any>[]>(
    () => [
      {
        id: "select",
        header: () => {
          const filteredRows = tableInstance?.getRowModel().rows.map(row => row.original) || [];
          const filteredSelectedRows = selectedRows.filter(row => filteredRows.includes(row));

          return (
            <input
              type="checkbox"
              onChange={(e) => {
                if (e.target.checked) {
                  // Add filtered rows to selected rows, avoiding duplicates
                  const newSelections = [...new Set([...selectedRows, ...filteredRows])];
                  setSelectedRows(newSelections);
                } else {
                  // Remove filtered rows from selected rows
                  const newSelections = selectedRows.filter(row => !filteredRows.includes(row));
                  setSelectedRows(newSelections);
                }
              }}
              checked={filteredRows.length > 0 && filteredSelectedRows.length === filteredRows.length}
              ref={(el) => {
                if (el) {
                  el.indeterminate = filteredSelectedRows.length > 0 && filteredSelectedRows.length < filteredRows.length;
                }
              }}
            />
          );
        },
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={selectedRows.includes(row.original)}
            onChange={() => handleRowSelect(row.original)}
            disabled={row.original.status != 'generated' || !row.original.key}
          />
        ),
      },
      {
        accessorFn: (row) => row.name,
        id: "Nome",
        header: t('partner.certificates_table_name'),
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.email,
        id: "Email",
        header: "EMAIL",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => formatDate(`${row.issueDate}`),
        id: "Emitido em",
        header: t('partner.certificates_table_date'),
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => formatDate(`${row.expirationDate}`),
        id: "Expira em",
        header: t('partner.certificates_table_expiration'),
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => handleStatus(row.status || CertificateStatus.WAITING),
        id: "Status",
        header: "STATUS",
        cell: (info) => <Tag size='sm' colorScheme={handleColor(info.getValue()) || 'gray'}>{t(info.getValue())}</Tag>,
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row,
        id: "Ações",
        enableColumnFilter: false,
        enableHiding: true,
        header: t('table.actions'),
        cell: (row) => {
          return (
            <CertificateActions
              downloadAction={() => download(row.getValue().id)}
              generateAction={() => genCertificate(row.getValue().id)}
              report={row.getValue()}
            />
          );
        },
      },
    ],
    [certificates, selectedRows, t, tableInstance]
  );

  useEffect(() => {
    loadCertificates();
  }, [updateData]);

  function closeModal() {
    onClose();
    setCertificate(initialCertificate);
  }

  async function loadCertificates() {
    setIsLoading(true);
    try {
      const response = await getCertificates();
      setCertificates(response.message);
      setIsLoading(false);
    } catch (error) {
      setCertificates([]);
      setIsLoading(false);
    }
  }

  async function genCertificate(certId: number) {
    try {
      const response = await generateCertificate(certId);
      setUpdateData(new Date().toLocaleString());
      openUrl(response.message)
    } catch (error) {
    }
  }

  async function download(certId: number) {
    try {
      const response = await getCertificateUrl(certId);
      openUrl(response.message)
    } catch (error) {
    }
  }

  const handleDeleteCertificate = async () => {
    toast({
      title: t('general.deleting') || 'Deleting...',
      status: 'loading',
      isClosable: true,
    });
    try {
      await deleteCertificate(certificate.id!);
      setUpdateData(new Date().toLocaleString());
      closeModal();
    } catch (error) {
      toast.closeAll();
      toast({
        title: t('general.error') || 'Error',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  };

  async function postBatchDownload() {
    setMassDownloading(true);
    try {
      const filteredRows = selectedRows.filter(row => row.status === 'generated');
      const hashes = filteredRows.map(row => row.key).filter((key): key is string => key !== undefined);

      const response = await batchDownload(hashes);
      const blob = new Blob([response], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      const currentDate = new Date().toLocaleDateString('en-GB').replace(/\//g, '-');
      link.href = url;
      link.download = `export_certificates_${currentDate}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setMassDownloading(false);
    } catch (error) {
      setMassDownloading(false);
      toast({
        title: t('general.invalid_csv') || 'Invalid CSV',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
    } else {
      toast({
        title: t('general.invalid_format') || 'Invalid Format',
        description: t('general.invalid_format_csv') || 'Please select a valid CSV file',
        status: 'error',
        isClosable: true,
      });
    }
  };

  const handleUpload = async () => {
    setIsSaving(true);
    toast({
      title: t('general.saving') || 'Saving...',
      status: 'loading',
      isClosable: true,
    });

    let formData = new FormData();
    formData.append('file', file!);

    for (const key in certificate) {
      if (Object.prototype.hasOwnProperty.call(certificate, key)) {
        const value = certificate[key as keyof ICertificate];
        if (value !== undefined) {
          formData.append(key, String(value));
        }
      }
    }

    try {
      await batchCertificate(formData);
      toast({
        title: t('general.success') || 'Success',
        status: 'success',
        isClosable: true,
      });
      setFile(null);
      setIsSaving(false);
      setUpdateData(new Date().toLocaleString());
      closeModal();
    } catch (error) {
      setIsSaving(false);
      toast({
        title: t('general.invalid_csv') || 'Invalid CSV',
        status: 'error',
        isClosable: true,
      });
    }
  };

  function RenderModal() {
    switch (modalType) {
      case "new":
        return (
          <Modal isOpen={isOpen} onClose={closeModal}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('partner.certificates_lot') || 'Batch Certificates'}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Flex direction={"column"}>
                  <UploadFileInput
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    id="file-upload"
                  />
                  <Alert status='info' mt={4}>
                    <AlertIcon />
                    <Box>
                      <AlertTitle>
                        <a href="https://bemap.sfo3.cdn.digitaloceanspaces.com/bemap_import_certificates.xlsx" target="_blank" rel="noopener noreferrer">
                          {t('partner.certificates_alert_title') || 'Download Template'}
                        </a>
                      </AlertTitle>
                      <AlertDescription>
                        {t('partner.certificates_alert_text') || 'Download the template to see the required format'}
                      </AlertDescription>
                    </Box>
                  </Alert>
                </Flex>
              </ModalBody>

              <ModalFooter gap="10px">
                <Button variant={"outline"} onClick={closeModal}>
                  {t('actions.cancel') || 'Cancel'}
                </Button>
                <Button colorScheme={"blue"} onClick={handleUpload} isLoading={isSaving} isDisabled={!file}>
                  {t('partner.certificates_alert_save') || 'Save'}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );
      case 'delete':
        return (
          <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={closeModal}
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                  {t('partner.certificates_delete') || 'Delete Certificate'}
                </AlertDialogHeader>

                <AlertDialogBody>
                  {t('actions.delete_confirm') || 'Are you sure you want to delete this certificate?'}
                </AlertDialogBody>

                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={closeModal}>
                    {t('actions.cancel') || 'Cancel'}
                  </Button>
                  <Button colorScheme='red' onClick={handleDeleteCertificate} ml={3}>
                    {t('actions.delete_confirm_button') || 'Delete'}
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        );
      default:
        return null;
    }
  }

  if (isLoading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <>
      <VStack gap={4} align="stretch" w="100%">
        <PageHeading title={t('partner.certificates')} actions={[
          {
            label: t('partner.certificates_new'),
            icon: <AddIcon />,
            isLoading: isSaving,
            onClick: () => {
              setModalType("new");
              onOpen();
            }
          },
          {
            label: t('analyst.download_mass'),
            icon: <MdDownload />,
            variant: "outline",
            isLoading: massDownloading,
            isDisabled: selectedRows.length < 1,
            onClick: () => {
              postBatchDownload();
            }
          }
        ]} />

        <Panel>
          <Table
            tableId="list-certificates-table"
            columns={columns}
            data={certificates}
            onTableReady={setTableInstance}
          />
        </Panel>
      </VStack>
      {RenderModal()}
    </>
  );
};

export default CertificatesList; 